import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Utils from "../utils/utils";
import JsonHelper from "../utils/jsonHelper";
import Permission from "../services/permission";

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

const getFilteredPermissions = ({ domain, environment, deployment, type, permissions }) => {
    if (deployment) {
        return permissions?.filter((perm) =>
            perm.deployment === deployment &&
            perm.type === type
        );
    }

    if (environment) {
        return permissions?.filter((perm) =>
            perm.environment === environment &&
            perm.type === type
        );
    }

    return permissions?.filter((perm) =>
        perm.domain === domain.toString() &&
        perm.type === type
    );
}

const getCrudPermissions = ({ domain, environment, deployment, type }) => {
    const user = JSON.parse(window.sessionStorage.getItem("user")) || {};
    let crud = {};
    if (user.appAdmin || user.adminForDomains?.includes(domain)) {
        crud = {
            create: true,
            read: true,
            update: true,
            delete: true
        };
    } else {
        let permissionType = type === "auditTrail" && Utils.isRootDomain() ? "domain" : type;

        const filteredPermissions = getFilteredPermissions({
            domain,
            environment,
            deployment,
            type: permissionType,
            permissions: user.uiPermissions
        });

        crud = {
            create: filteredPermissions.find(perm => perm.action === "create") !== undefined,
            read: filteredPermissions.find(perm => perm.action === "view") !== undefined,
            update: filteredPermissions.find(perm => perm.action === "edit") !== undefined,
            delete: filteredPermissions.find(perm => perm.action === "edit") !== undefined
        }

        if (permissionType === "user") {
            crud.create = filteredPermissions.find(perm => perm.action === "edit") !== undefined;
        }
    }

    return crud;
}

const useHasPermission = ({ domain, environment, deployment, type }) => {
    const [permissions, setPermissions] = useState({});

    useEffect(() => {
        if (domain && type) {
            let crud = getCrudPermissions({ domain, environment, deployment, type });
            setPermissions(crud);
        }
    }, [domain, environment, deployment, type]);

    return permissions;
};

const useHasEnvironmentsPermission = ({ domain, environments, deployment, type }) => {
    const [permissions, setPermissions] = useState({});

    useEffect(() => {
        if (domain && type && environments && environments.length > 0) {
            setPermissions((permissions) => {
                permissions = [];
                environments.forEach((environment) => {
                    permissions[environment._id] = getCrudPermissions({ domain, environment: environment._id, deployment, type })
                });

                return permissions;
            });

        }
    }, [domain, environments, deployment, type]);

    return permissions;
};

const usePermissionsInDomain = ({ domainId }) => {
    const [allPermissions, setAllPermissions] = useState([]);

    const getPermissions = useCallback(async (permissionsForDomainId) => {
        const response = await Permission.getUiPermissionsForDomain(permissionsForDomainId);
        if (!response.error) {
            setAllPermissions(response.data);
        }
    }, []);

    useEffect(() => {
        if (domainId) {
            getPermissions(domainId);
        }
        else {
            getPermissions(Utils.getDomain());
        }
    }, [domainId, getPermissions]);

    return allPermissions;
};

const useGetDomain = () => {
    const [domain, _setDomain] = useState(() => {
        const domain = window.sessionStorage.getItem("domain") || "{}";
        return JSON.parse(domain);
    });

    const setDomain = (newDomain) => {
        window.sessionStorage.setItem("domain", JSON.stringify(newDomain));
        _setDomain(newDomain);
    };

    return [domain, setDomain];
};

const useGetUser = () => {
    const [user, _setUser] = useState(() => {
        const user = window.sessionStorage.getItem("user") || "{}";
        return JSON.parse(user);
    });

    const setUser = (newUser) => {
        window.sessionStorage.setItem("user", JSON.stringify(newUser));
        _setUser(newUser);
    };

    return [user, setUser];
};

const useInitialBuildTimeConfig = (id) => {
    const key = `initalBuildTimeConfig_${id}`;
    const [value, _setValue] = useState(() => {
        const value = window.sessionStorage.getItem(key);
        return JsonHelper.isJsonString(value) ? JSON.parse(value) : "";
    });

    const setValue = (newValue) => {
        window.sessionStorage.setItem(key, JSON.stringify(newValue, null, '\t'));
        _setValue(newValue);
    }

    return [value, setValue];
}

const useBuildTimeConfig = (id) => {
    const key = `buildTimeConfig_${id}`;
    const [value, _setValue] = useState(() => {
        const value = window.sessionStorage.getItem(key);
        return JsonHelper.isJsonString(value) ? JSON.parse(value) : "";
    });

    const setValue = (newValue) => {
        window.sessionStorage.setItem(key, JSON.stringify(newValue, null, '\t'));
        _setValue(newValue);
    }

    return [value, setValue];
}

const useTemplateConfig = (id) => {
    const key = `templateConfig_${id}`;
    const [value, _setValue] = useState(() => {
        const value = window.sessionStorage.getItem(key);
        return JsonHelper.isJsonString(value) ? JSON.parse(value) : "";
    });

    const setValue = (newValue) => {
        window.sessionStorage.setItem(key, JSON.stringify(newValue, null, '\t'));
        _setValue(newValue);
    }

    return [value, setValue];
}

const useTemplateMetadata = (id) => {
    const key = `templateMetadata_${id}`;
    const [value, _setValue] = useState(() => {
        const value = window.sessionStorage.getItem(key);
        return JsonHelper.isJsonString(value) ? JSON.parse(value) : "";
    });

    const setValue = (newValue) => {
        window.sessionStorage.setItem(key, JSON.stringify(newValue, null, '\t'));
        _setValue(newValue);
    }

    return [value, setValue];
}

export {
    getCrudPermissions,
    useQuery,
    useHasPermission,
    useHasEnvironmentsPermission,
    usePermissionsInDomain,
    useGetDomain,
    useGetUser,
    useInitialBuildTimeConfig,
    useBuildTimeConfig,
    useTemplateConfig,
    useTemplateMetadata
};